import * as React from 'react';
import { useSelector } from 'react-redux';

import { isEmpty, last, concat } from 'ramda';
import { EStepCode, TFormStoreData, TNextStep, TNextStepArguments } from '../../../types/form';
import {
  IStepsHistoryItem,
  TAnswersState,
  THandleFormSubmit,
  TQuestionMap,
  TSetAnswer,
  TStepsMap,
} from '../types/internal';
import { getNextStep } from './getNextStep';
import { getProgressBarData } from './getProgressBarData';
import { useQuizCompleteControls } from './useQuizCompleteControls';
import { useBackInterceptor } from './useBackIntercaptors';
import { getQuestionsMap } from './getQuestionsMap';
import { getStepsMap } from './getStepsMap';
import { prepareCurrentQuestion } from './prepareCurrentQuestion';
import { mapNextStepToHistoryItem } from './mapNextStepToHistoryItem';
import { getPrevStep } from './getPrevStep';
import { useDeviceType } from '@cian/ui-kit';
import { trackPrevStepEvent } from '../../../utils/useFormTracking/tracking';
import { trackStartQuizEvent } from '../tracking';
import { useSetWebViewUrl } from '../../../utils/useSetWebViewUrl';
import { IApplicationState } from '../../../types/redux';
import { getFirstStepCode } from './getFirstStepCode';
import { parseInitialAnswersState, parseSkipSteps } from './parseInitialAnswersState';
import { selectAnonymousUserId } from '../../../selectors/selectUser';

export const useFormController = (formData: TFormStoreData, handleSubmit: THandleFormSubmit) => {
  const deviceType = useDeviceType();
  const queryParams = useSelector(state => state.page.queryParams);
  const anonymousUserId = useSelector(selectAnonymousUserId);
  const questionsMap = React.useMemo<TQuestionMap>(() => getQuestionsMap(formData), [formData]);
  const stepsMap = React.useMemo<TStepsMap>(() => getStepsMap(formData), [formData]);
  const { setStepQuery } = useSetWebViewUrl();
  const withSaleRentIntroScreen = useSelector((state: IApplicationState) => state.intro.fromSaleRent);

  const [stepsToSkip, setStepsToSkip] = React.useState<EStepCode[]>(
    parseSkipSteps({
      skipSteps: formData.skipSteps,
      formDataFromQs: { skipSteps: queryParams.skipSteps, formData: queryParams.formData },
    }),
  );

  const [answersState, setAnswersState] = React.useState<TAnswersState>(
    parseInitialAnswersState({
      userData: formData.userData,
      formDataFromQs: { formData: queryParams.formData },
    }),
  );

  /**
   * Хранение текущего шага формы
   * Включено вычисление 1го шага формы
   * */
  const firstStepCode = React.useMemo(
    () => getFirstStepCode({ formData, answersState, stepsToSkip }),
    [formData, answersState, stepsToSkip],
  );
  const [currentStepCode, setCurrentStepCode] = React.useState<EStepCode>(firstStepCode);

  /**
   * История пройденных шагов
   * В нее так-же включаются пропущенные шаги (skipSteps)
   * Начальное значение вычисляется, т.к первый шаг (formData.schema.firstStepCode) может быть пропущен через skipSteps
   * */
  const [stepsHistory, setStepsHistory] = React.useState<IStepsHistoryItem[]>([
    { stepCode: firstStepCode, stepArgs: null },
  ]);

  const [currentStepArgs, setCurrentStepArgs] = React.useState<TNextStepArguments | null | undefined>(null);
  const [quizStarted, setQuizStarted] = React.useState(false);
  const quizCompleteControls = useQuizCompleteControls(handleSubmit);
  const { backInterceptor, registerBackInterceptor, unRegisterBackInterceptor } = useBackInterceptor();

  /** Флаг, позволяющий следить, какой из переходов произошел последним - к следующему или предыдущему вопросу */
  const [isSetPrev, setPrev] = React.useState(false);

  /**
   * Объект шага находится из известного текущего кода шага,
   * а объект вопроса находится из questionCode в текущем шагу */
  const currentStep = stepsMap[currentStepCode];
  const currentQuestion = prepareCurrentQuestion({
    currentStep,
    currentStepArgs,
    questionsMap,
    withSaleRentIntroScreen,
  });

  /**
   * _answersState специально передается в аргументе извне, т.к answersState не всегда успевает обновиться
   * это происходит, когда в компоненте подряд вызывается setAnswers, setNextStep (напр в Radio)
   * */
  const setNextStep = (_answersState: TAnswersState) => {
    const steps = getNextStep({
      currentStep,
      answersState: _answersState,
      stepsToSkip,
      formData,
    });

    if (!steps || isEmpty(steps)) {
      return;
    }

    const nextStep = last(steps) as TNextStep;

    setCurrentStepCode(nextStep.nextStepCode);
    setStepQuery(nextStep.nextStepCode);
    setCurrentStepArgs(nextStep.nextStepArgs);
    setStepsHistory(prev => concat(prev, steps.map(mapNextStepToHistoryItem)));
    setPrev(false);
  };

  const setPrevStep = () => {
    if (backInterceptor) {
      const shouldPassThrough = backInterceptor();
      if (!shouldPassThrough) {
        return;
      }
    }

    const prevStep = getPrevStep({ stepsToSkip, stepsHistory });

    trackPrevStepEvent({ field: currentQuestion.answerField, answersState, deviceType, anonymousUserId });

    if (prevStep) {
      const { prevHistoryItem, slicedHistory } = prevStep;

      setAnswersState({
        ...answersState,
        [currentQuestion.answerField]: undefined,
      });
      setStepsHistory(slicedHistory);
      setCurrentStepCode(prevHistoryItem.stepCode);
      setCurrentStepArgs(prevHistoryItem.stepArgs);
      setStepQuery(prevHistoryItem.stepCode);
      setPrev(true);
    }
  };

  const setAnswer = React.useCallback<TSetAnswer>(newAnswer => {
    setAnswersState(answersState => {
      const answerContainer = answersState[newAnswer.answerField] || {};

      return {
        ...answersState,
        [newAnswer.answerField]: {
          ...answerContainer,
          ...newAnswer,
        },
      };
    });
  }, []);

  const startQuiz = React.useCallback(() => {
    setQuizStarted(true);
    trackStartQuizEvent(deviceType);
  }, [deviceType]);

  const progressbarData = getProgressBarData({ formData, currentStep, stepsHistory });

  return {
    currentQuestion,
    setNextStep,
    setPrevStep,
    setAnswer,
    answersState,
    setAnswersState,
    quizStarted,
    startQuiz,
    isSetPrev,
    registerBackInterceptor,
    unRegisterBackInterceptor,
    ...progressbarData,
    ...quizCompleteControls,
    stepsToSkip,
    setStepsToSkip,
    questionsMap,
    stepsMap,
    stepsHistory,
  };
};
